import { ColorEnum } from '@/constants/color'
import Button from '@/features/openTabs/components/AccountActionButton'
import AccountDetail from '@/features/openTabs/components/AccountDetail'
import AccountHeader from '@/features/openTabs/components/AccountHeader'
import AccountList from '@/features/openTabs/components/AccountList'
import CatalogFooter from '@/features/openTabs/components/CatalogFooter'
import CreateAccountModal from '@/features/openTabs/components/CreateAccountModal'
import DraftCart from '@/features/openTabs/components/DraftCart'
import EmptyState from '@/features/openTabs/components/EmptyState'
import ProductCatalog from '@/features/openTabs/components/ProductCatalog'
import { useOpenTabConfigurableProductModal } from '@/features/openTabs/hooks/useOpenTabConfigurableProductModal'
import useOpenTabsPage from '@/features/openTabs/pages/OpenTabsPage/hooks/useOpenTabsPage'
import { Provider as ProductConfigurationModalPortalProvider } from '@/features/pos/components/ProductConfigurationModal/contexts/Portal'
import { PrintJobType } from '@/features/print/constants'
import { usePrintDocument } from '@/features/print/hooks/usePrintDocument'
import Typography from '@/features/ui/typography'
import { useScreenLayout } from '@/hooks/screen'
import { FC, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ActivityIndicator, View } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

const ProductConfiguration = () => {
    useOpenTabConfigurableProductModal()
    return null
}

const OpenTabsPage: FC = () => {
    const { t } = useTranslation('app')
    const layout = useScreenLayout()
    const { styles } = useStyles(stylesheet)
    const { print, printing } = usePrintDocument()
    const {
        activeDetailTab,
        setActiveDetailTab,
        events,
        accountItems,
        accountsTotal,
        activeStatus,
        accountName,
        catalogCategories,
        catalogSections,
        catalogMode,
        compactCatalogReview,
        setCompactCatalogReview,
        catalogCartItems,
        handleDraftItemEdit,
        removeCatalogItem,
        changeCatalogItemQuantity,
        submittedItemsBusy,
        submittedItemsRefreshFailed,
        retrySubmittedItemsRefresh,
        handleSubmittedItemQuantityChange,
        handleSubmittedItemRemove,
        catalogTotalPrice,
        catalogPriceItems,
        catalogLoading,
        catalogCartRefreshFailed,
        retryCatalogCart,
        listFailed,
        retry,
        detailFailed,
        detailRefreshFailed,
        catalogFailed,
        catalogHasSelection,
        canSettleAccount,
        creatingAccount,
        renamingAccount,
        detailLoading,
        loading,
        submittingCatalogCart,
        catalogBusy,
        closedAccountCount,
        modalMode,
        openAccountCount,
        orders,
        selectedAccount,
        selectedAccountSummary,
        selectedAccountId,
        selectedAccountTotal,
        selectedAccountPriceItems,
        selectedCategoryId,
        selectedProductIds,
        closeModal,
        handleAccountSelect,
        handleCatalogConfirm,
        handleDetailClose,
        handleModalConfirm,
        handleProductSelect,
        handleStatusChange,
        openCreateModal,
        openRenameModal,
        resetCatalog,
        setAccountName,
        setCatalogMode,
        setSelectedCategoryId,
        openPayAll,
        openSplit,
    } = useOpenTabsPage()
    const accountCounts = useMemo(
        () => ({ open: openAccountCount, closed: closedAccountCount }),
        [closedAccountCount, openAccountCount]
    )

    const reprintDisabled = printing || submittedItemsBusy || detailRefreshFailed
    const handleReprint = useCallback(
        (submissionUuid: string) => {
            if (reprintDisabled) return

            void print({ printType: PrintJobType.OpenTabSubmissionBon, submissionUuid })
        },
        [print, reprintDisabled]
    )

    const errorState = (
        <View style={styles.emptyPanel}>
            <EmptyState title={t('errors.errorHappenedTitle')} description={t('unexpectedError')} />
        </View>
    )
    const retryError = (
        <View style={styles.retryError}>
            <Typography.Description>{t('errors.errorHappenedTitle')}</Typography.Description>
            <Button onPress={retry} disabled={loading || detailLoading}>
                {t('openTabs.actions.retry')}
            </Button>
        </View>
    )
    const pendingState = (
        <View style={styles.loadingPanel}>
            <ActivityIndicator accessibilityLabel={t('openTabs.loading')} />
        </View>
    )

    const catalogRefreshError = catalogCartRefreshFailed && (
        <View style={styles.retryError}>
            <Typography.Description>{t('openTabs.feedback.refreshFailed')}</Typography.Description>
            <Button onPress={retryCatalogCart}>{t('openTabs.actions.retry')}</Button>
        </View>
    )
    const catalog = (
        <View style={styles.pendingPanel}>
            {catalogRefreshError}
            {catalogLoading ? (
                pendingState
            ) : catalogFailed ? (
                errorState
            ) : (
                <ProductCatalog
                    disabled={catalogBusy}
                    categories={catalogCategories}
                    sections={catalogSections}
                    selectedCategoryId={selectedCategoryId}
                    selectedProductIds={selectedProductIds}
                    onCategorySelect={setSelectedCategoryId}
                    onProductSelect={handleProductSelect}
                />
            )}
        </View>
    )

    const list = (
        <View style={styles.pendingPanel}>
            {listFailed && retryError}
            {loading && !accountItems.length ? (
                pendingState
            ) : (
                <AccountList
                    accounts={accountItems}
                    status={activeStatus}
                    counts={accountCounts}
                    selectedAccountId={selectedAccountId ?? undefined}
                    total={accountsTotal}
                    onStatusChange={handleStatusChange}
                    onAccountSelect={handleAccountSelect}
                    onCreateAccount={openCreateModal}
                />
            )}
        </View>
    )

    const detail =
        detailLoading || detailFailed ? (
            <View style={styles.pendingPanel}>
                {layout === 'compact' && selectedAccountSummary && (
                    <AccountHeader
                        accountNumber={selectedAccountSummary.number}
                        accountName={selectedAccountSummary.name}
                        closed={selectedAccountSummary.status === 'closed'}
                        onClose={handleDetailClose}
                    />
                )}
                {detailLoading ? pendingState : retryError}
            </View>
        ) : selectedAccount ? (
            <View style={styles.pendingPanel}>
                {detailRefreshFailed && retryError}
                <AccountDetail
                    accountNumber={selectedAccount.number}
                    accountName={selectedAccount.name}
                    closed={selectedAccount.status === 'closed'}
                    orders={orders}
                    events={events}
                    activeTab={activeDetailTab}
                    onTabChange={setActiveDetailTab}
                    onReprint={handleReprint}
                    reprintDisabled={reprintDisabled}
                    totalPrice={selectedAccountTotal ?? ''}
                    priceItems={selectedAccountPriceItems}
                    catalogMode={catalogMode}
                    catalogHasSelection={catalogHasSelection}
                    catalogSubmitting={submittingCatalogCart}
                    catalogBusy={catalogBusy}
                    catalogRefreshFailed={catalogCartRefreshFailed}
                    onRetryCatalogRefresh={retryCatalogCart}
                    catalogTotalPrice={catalogTotalPrice}
                    catalogPriceItems={catalogPriceItems}
                    draftCart={
                        <DraftCart
                            items={catalogCartItems}
                            disabled={catalogBusy}
                            onEdit={handleDraftItemEdit}
                            onRemove={removeCatalogItem}
                            onChangeQuantity={changeCatalogItemQuantity}
                        />
                    }
                    submittedItemsBusy={submittedItemsBusy}
                    submittedItemsRefreshFailed={submittedItemsRefreshFailed}
                    onRetrySubmittedItemsRefresh={retrySubmittedItemsRefresh}
                    onChangeItemQuantity={handleSubmittedItemQuantityChange}
                    onRemoveItem={handleSubmittedItemRemove}
                    canPay={canSettleAccount}
                    onRename={renamingAccount ? undefined : openRenameModal}
                    onClose={handleDetailClose}
                    onAddOrder={() => {
                        if (!submittedItemsBusy) setCatalogMode(true)
                    }}
                    onPayAll={openPayAll}
                    onSplit={openSplit}
                    onCancelCatalog={resetCatalog}
                    onConfirmCatalog={handleCatalogConfirm}
                />
            </View>
        ) : (
            <View style={styles.emptyPanel}>
                <EmptyState
                    title={t('openTabs.empty.windowTitle')}
                    description={t('openTabs.empty.windowDescription')}
                />
            </View>
        )

    return (
        <View style={styles.container}>
            {layout === 'extended' ? (
                <>
                    <View style={styles.leftPanel}>
                        {catalogMode && selectedAccount ? (
                            <ProductConfigurationModalPortalProvider>
                                {catalog}
                                <ProductConfiguration />
                            </ProductConfigurationModalPortalProvider>
                        ) : (
                            list
                        )}
                    </View>
                    <View style={styles.rightPanel}>{detail}</View>
                </>
            ) : selectedAccountId ? (
                catalogMode ? (
                    <ProductConfigurationModalPortalProvider>
                        <View style={styles.compactCatalog}>
                            {compactCatalogReview ? (
                                <>{detail}</>
                            ) : (
                                <>
                                    {catalog}
                                    <CatalogFooter
                                        totalPrice={catalogTotalPrice}
                                        priceItems={catalogPriceItems}
                                        canConfirm={catalogHasSelection && !catalogBusy}
                                        submitting={submittingCatalogCart}
                                        onCancel={resetCatalog}
                                        confirmLabel={t('openTabs.actions.continue')}
                                        onConfirm={() => setCompactCatalogReview(true)}
                                    />
                                </>
                            )}
                        </View>
                        <ProductConfiguration />
                    </ProductConfigurationModalPortalProvider>
                ) : (
                    detail
                )
            ) : (
                list
            )}

            {modalMode && (
                <CreateAccountModal
                    name={accountName}
                    title={modalMode === 'rename' ? t('openTabs.renameAccount.title') : undefined}
                    confirmLabel={
                        modalMode === 'rename' ? t('openTabs.renameAccount.confirm') : undefined
                    }
                    loading={creatingAccount || renamingAccount}
                    onChange={setAccountName}
                    onConfirm={handleModalConfirm}
                    onCancel={closeModal}
                />
            )}
        </View>
    )
}

const stylesheet = createStyleSheet({
    container: {
        flex: 1,
        flexDirection: 'row',
        backgroundColor: ColorEnum.WHITE,
    },
    leftPanel: {
        flex: 1,
        minWidth: 0,
    },
    rightPanel: {
        flex: 1,
        minWidth: 0,
    },
    emptyPanel: {
        flex: 1,
        backgroundColor: ColorEnum.WHITE,
        boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.25)',
    },
    compactCatalog: {
        flex: 1,
    },
    pendingPanel: {
        flex: 1,
        backgroundColor: ColorEnum.WHITE,
    },
    loadingPanel: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
    },
    retryError: {
        padding: 16,
        gap: 8,
        backgroundColor: ColorEnum.VIOLET_100,
    },
})

export default OpenTabsPage
